// The player's session and the details remembered on this device (spec v2 section 2).
// Storage can throw (private mode, blocked site data) or come back empty (Instagram's browser), so every access is guarded
// and the token also lives in the URL.

export interface Session { code: string; token: string; playerId: string; name: string }
/** Join details remembered on this device so the next battle is one tap. */
export interface Remembered { name: string; email: string; marketing: boolean }
/** What the join form holds while someone types, so an accidental refresh does not wipe it. */
export interface JoinDraft { code: string; name: string; email: string; marketing: boolean }

const KEY = 'rounds.session';
const ME = 'rounds.me';
const DRAFT = 'rounds.draft';
const REF = 'rounds.ref';
const safe = <T>(fn: () => T): T | undefined => { try { return fn(); } catch { return undefined; } };
const str = (v: unknown): v is string => typeof v === 'string';

// --- Kept values ----------------------------------------------------------------------------------------------------
// Instagram's browser can wipe storage when the viewer flips back to the Live, and a private window can refuse it, so
// every value here is kept three ways: in memory for this page, in sessionStorage for this tab and in localStorage for
// this device, and read back in that order (the signed-in session keeps its own memory copy the same way, staff/session.ts).
const held = new Map<string, string>();
function keep(key: string, value: string) { held.set(key, value); safe(() => sessionStorage.setItem(key, value)); safe(() => localStorage.setItem(key, value)); }
function kept(key: string): string | undefined { return held.get(key) ?? safe(() => sessionStorage.getItem(key)) ?? safe(() => localStorage.getItem(key)) ?? undefined; }
function forget(key: string) { held.delete(key); safe(() => sessionStorage.removeItem(key)); safe(() => localStorage.removeItem(key)); }

export function saveSession(s: Session) {
  keep(KEY, JSON.stringify(s));
  // Keep the token in the URL too: Instagram's browser can wipe storage when the viewer switches away.
  const u = new URL(window.location.href); u.searchParams.set('t', s.token); u.searchParams.set('c', s.code); u.searchParams.set('p', s.playerId); u.searchParams.set('n', s.name);
  safe(() => window.history.replaceState(window.history.state, '', u.toString()));
}
export function tokenFromUrl(): Session | undefined {
  const u = new URL(window.location.href); const t = u.searchParams.get('t'); const c = u.searchParams.get('c'); const p = u.searchParams.get('p');
  return t && c && p ? { token: t, code: c, playerId: p, name: u.searchParams.get('n') ?? '' } : undefined;
}
function parseSession(raw: string | null | undefined): Session | undefined {
  if (!raw) return undefined;
  const s = safe(() => JSON.parse(raw) as Partial<Session>);
  return s && str(s.code) && str(s.token) && str(s.playerId) ? { code: s.code, token: s.token, playerId: s.playerId, name: str(s.name) ? s.name : '' } : undefined;
}
export function loadSession(): Session | undefined {
  const fromUrl = tokenFromUrl(); if (fromUrl) return fromUrl;
  return parseSession(kept(KEY));
}
export function clearSession() { forget(KEY); }
/** The session stored on this device, only if it belongs to this battle. */
export function sessionFor(code: string): Session | undefined {
  const s = loadSession();
  return s && code && s.code.toUpperCase() === code.toUpperCase() ? s : undefined;
}

// --- The result link (5 Oct review, R1) ---------------------------------------------------------------------------------
// The link a player copies or forwards opens their result read-only: it carries the token GET /me hands to the session
// (MeResponse.resultToken), never the session itself, which can answer and change the claim form. On the result page the
// claim form saves with the session this device holds for that player (ownSession), or asks for the email they joined with.

/** The result page's address for a result link's token. */
export const resultPath = (resultToken: string) => `/r/${encodeURIComponent(resultToken)}`;
/** The session this device holds for this player of this battle: the one in memory (`held`) first, then the stored one. */
export function ownSession(code: string, playerId: string, held?: Session): Session | undefined {
  const mine = (s: Session | undefined) => (s && code && s.playerId === playerId && s.code.toUpperCase() === code.toUpperCase() ? s : undefined);
  return mine(held) ?? mine(sessionFor(code));
}

/** 9 Oct: kept the way the game session is (memory, this tab, this device): the joined address is what a sign-in proves. */
export function saveMe(m: Remembered) {
  keep(ME, JSON.stringify({ name: m.name.trim(), email: m.email.trim(), marketing: m.marketing === true }));
}
export function loadMe(): Remembered | undefined {
  const raw = kept(ME); if (!raw) return undefined;
  const m = safe(() => JSON.parse(raw) as Partial<Remembered>);
  return m && str(m.name) && str(m.email) && m.name.trim() && m.email.trim() ? { name: m.name, email: m.email, marketing: m.marketing === true } : undefined;
}
export function clearMe() { forget(ME); }

export function saveDraft(d: JoinDraft) {
  if (!d.name.trim() && !d.email.trim()) { clearDraft(); return; }
  keep(DRAFT, JSON.stringify({ code: d.code, name: d.name, email: d.email, marketing: d.marketing === true }));
}
function parseDraft(raw: string | null | undefined): JoinDraft | undefined {
  if (!raw) return undefined;
  const d = safe(() => JSON.parse(raw) as Partial<JoinDraft>);
  return d && str(d.name) && str(d.email) ? { code: str(d.code) ? d.code : '', name: d.name, email: d.email, marketing: d.marketing === true } : undefined;
}
export function loadDraft(): JoinDraft | undefined {
  return parseDraft(kept(DRAFT));
}
export function clearDraft() { forget(DRAFT); }

// --- Where the player came from (the join link's ?ref=, for example an influencer's handle) ---------------------------
// The server keeps letters, digits, '_' and '-' up to 32 characters; the phone sends exactly that. The ref is kept on the
// device for the battle it arrived with, so it survives a refresh, a trip through remember-me and the game sending a player
// back to the join page (whose link no longer carries it).

/** Letters, digits, '_' and '-' only, at most 32 characters; undefined when nothing is left. */
export function cleanRef(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const ref = v.trim().replace(/[^A-Za-z0-9_-]/g, '').slice(0, 32);
  return ref || undefined;
}
/** The ?ref= of the page's own link, cleaned. */
export function refFromUrl(): string | undefined {
  return cleanRef(safe(() => new URL(window.location.href).searchParams.get('ref')));
}
/** A stored ref older than this is not used: a join link opened yesterday does not credit today's battle. */
export const REF_MAX_AGE_MS = 24 * 3_600_000;
interface StoredRef { ref: string; code: string; at: number }
/** Remembers a ref for a battle ('' while the code is not known yet: the root link with ?ref=). */
export function saveRef(ref: string | undefined, code = '') {
  const r = cleanRef(ref); if (!r) return;
  keep(REF, JSON.stringify({ ref: r, code: code.toUpperCase(), at: Date.now() } satisfies StoredRef));
}
function parseRef(raw: string | null | undefined): StoredRef | undefined {
  if (!raw) return undefined;
  const v = safe(() => JSON.parse(raw) as Partial<StoredRef>);
  const ref = cleanRef(v?.ref);
  return v && ref && str(v.code) && typeof v.at === 'number' ? { ref, code: v.code, at: v.at } : undefined;
}
/** The ref remembered for this battle (or for whichever battle comes next, when it arrived without a code). */
export function loadRef(code: string): string | undefined {
  const s = parseRef(kept(REF));
  if (!s || Date.now() - s.at > REF_MAX_AGE_MS) return undefined;
  return !s.code || s.code === code.toUpperCase() ? s.ref : undefined;
}
export function clearRef() { forget(REF); }

// --- The end-of-battle step (9 Oct) -----------------------------------------------------------------------------------
// The details step between the podium and the result is shown once per player of a battle: once it has been on screen,
// the podium's button goes straight to the result, whatever the player did there (saved, or Not now). Kept the way the
// session is, for this battle and player only.
const ASKED = 'rounds.asked';
const askedKey = (code: string, playerId: string) => `${code.toUpperCase()}:${playerId}`;
/** Notes that this player of this battle has seen the details step. */
export function markDetailsAsked(code: string, playerId: string) { keep(ASKED, askedKey(code, playerId)); }
/** Whether this player of this battle has seen the details step on this device. */
export function detailsAsked(code: string, playerId: string): boolean { return kept(ASKED) === askedKey(code, playerId); }
export function clearDetailsAsked() { forget(ASKED); }

// 14 Oct: the details are asked once per phone across a day of battles. Once saved here for an address, later battles
// on this phone joined with that address skip the step and the offer: MedSnapp already holds them on the identity
// (ID#<email>), and the event export joins them by address. A different address on the same phone is asked as usual.
const GIVEN = 'rounds.given';
/** How long a save on this phone stands in for the next battles' details step. */
export const DETAILS_GIVEN_FOR_MS = 24 * 3_600_000;
const addressKey = (email: string | undefined) => (email ?? '').trim().toLowerCase();
/** Notes that details were saved on this phone for this address. */
export function markDetailsGiven(email: string | undefined, now: number = Date.now()) {
  const e = addressKey(email); if (e) keep(GIVEN, JSON.stringify({ email: e, at: now }));
}
/** Whether details were saved on this phone for this address within DETAILS_GIVEN_FOR_MS. */
export function detailsGiven(email: string | undefined, now: number = Date.now()): boolean {
  const e = addressKey(email); const raw = kept(GIVEN); if (!e || !raw) return false;
  const v = safe(() => JSON.parse(raw) as { email?: unknown; at?: unknown });
  return Boolean(v && v.email === e && typeof v.at === 'number' && v.at <= now + 60_000 && now - v.at < DETAILS_GIVEN_FOR_MS);
}
export function clearDetailsGiven() { forget(GIVEN); }
